/* Existing premium delivery engine; merchant-confirmed daily midnight cycle in Europe/Paris.
 * Standard 4–8 days includes preparation. Absolute deadlines never reset with the visit. */
((root) => {
  const parisParts = date => Object.fromEntries(new Intl.DateTimeFormat('en-CA', {timeZone:'Europe/Paris',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(date).filter(p=>p.type!=='literal').map(p=>[p.type,Number(p.value)]));
  const iso = day => day.toISOString().slice(0,10);
  function selectRules(defaults, product, variantId) {
    if (!defaults?.enabled || product?.disabled) return null;
    const override = product?.variants?.[String(variantId)];
    if (override && !override.verified) return null;
    const logisticsKeys=['dispatch_workdays','dispatch_min_workdays','dispatch_max_workdays','transit_min_workdays','transit_max_workdays','delivery_min_workdays','delivery_max_workdays'];
    const hasUnverifiedDetails = product && !product.verified && ['cutoff',...logisticsKeys].some(k=>product[k] != null);
    if (hasUnverifiedDetails) return null;
    const selected={...defaults};
    for(const source of [product?.verified?product:null,override]){
      if(!source)continue;
      // A verified specific dispatch/transit range must not inherit a shorter standard total.
      if(logisticsKeys.some(k=>source[k]!=null)){
        selected.next_day_dispatch_confirmed=false;
        selected.delivery_days_basis='business';
        if(source.delivery_min_workdays==null){delete selected.delivery_min_workdays;delete selected.delivery_max_workdays;}
      }
      Object.assign(selected,source);
    }
    return selected;
  }
  function deliveryWindow(rules){
    if(!rules?.verified)return null;
    const valid=n=>Number.isInteger(n)&&n>=0&&n<=60;
    const prepMin=rules.dispatch_workdays??rules.dispatch_min_workdays,prepMax=rules.dispatch_workdays??rules.dispatch_max_workdays;
    if(!valid(prepMin)||!valid(prepMax)||prepMax<prepMin)return null;
    let min=rules.delivery_min_workdays,max=rules.delivery_max_workdays;
    if(min==null&&max==null){
      if(!valid(rules.transit_min_workdays)||!valid(rules.transit_max_workdays)||rules.transit_max_workdays<rules.transit_min_workdays)return null;
      min=prepMin+rules.transit_min_workdays;max=prepMax+rules.transit_max_workdays;
    }
    return valid(min)&&valid(max)&&max>=min?{min,max,prepMin,prepMax}:null;
  }
  function deliverySchedule(rules, now) {
    if (!rules?.verified || rules.time_zone !== 'Europe/Paris' || !/^\d{2}:\d{2}$/.test(rules.cutoff || '') || !Array.isArray(rules.non_working_dates)) return null;
    const [hour,minute]=rules.cutoff.split(':').map(Number);
    const minimum=rules.dispatch_workdays ?? rules.dispatch_min_workdays;
    const maximum=rules.dispatch_workdays ?? rules.dispatch_max_workdays;
    const duration=deliveryWindow(rules);
    if(hour>23 || minute>59 || !duration) return null;
    const parts=parisParts(now), day=new Date(Date.UTC(parts.year,parts.month-1,parts.day));
    if(!rules.dispatch_every_day&&(!rules.calendar_valid_until || rules.calendar_valid_until<iso(day))) return null;
    const closed=new Set([...rules.non_working_dates,...String(rules.extra_closed_dates || '').split(',').map(x=>x.trim()).filter(Boolean)]);
    const work=d=>rules.dispatch_every_day===true||(![0,6].includes(d.getUTCDay())&&!closed.has(iso(d)));
    const next=d=>{const v=new Date(d);v.setUTCDate(v.getUTCDate()+1);return v;};
    const add=(d,n)=>{let v=new Date(d),guard=0;while(n>0&&guard++<150){v=next(v);if(work(v))n--;}return n===0?v:null;};
    const addDelivery=n=>{let v=new Date(day),guard=0;while(n>0&&guard++<150){v=next(v);if(rules.delivery_days_basis==='calendar'||(![0,6].includes(v.getUTCDay())&&!closed.has(iso(v))))n--;}return n===0?v:null;};
    const from=addDelivery(duration.min),to=addDelivery(duration.max);
    if(!from||!to||(rules.delivery_days_basis!=='calendar'&&(!rules.calendar_valid_until||iso(to)>rules.calendar_valid_until)))return null;
    const beforeCutoff=parts.hour*60+parts.minute<hour*60+minute;
    let cutoffDay=new Date(day),guard=0;
    if(!beforeCutoff)cutoffDay=next(cutoffDay);
    while(!work(cutoffDay)&&guard++<150)cutoffDay=next(cutoffDay);
    if(!work(cutoffDay))return null;
    const desired=Date.UTC(cutoffDay.getUTCFullYear(),cutoffDay.getUTCMonth(),cutoffDay.getUTCDate(),hour,minute);
    let cutoff=new Date(desired);
    for(let i=0;i<3;i++){
      const p=parisParts(cutoff), shown=Date.UTC(p.year,p.month-1,p.day,p.hour,p.minute,p.second);
      cutoff=new Date(cutoff.getTime()+desired-shown);
    }
    const dispatchFrom=rules.next_day_dispatch_confirmed?next(day):add(cutoffDay,minimum),dispatchTo=add(cutoffDay,maximum);
    if(!dispatchFrom||!dispatchTo)return null;
    if((!rules.dispatch_every_day&&iso(dispatchTo)>rules.calendar_valid_until)||cutoff<=now)return null;
    const nextDayDispatch=rules.next_day_dispatch_confirmed===true||(work(next(day))&&iso(dispatchFrom)===iso(next(day))&&iso(cutoffDay)===iso(day)&&beforeCutoff);
    return {preparation:cutoffDay,dispatchFrom,dispatchTo,duration,from,to,cutoff,remaining:cutoff-now,countdownApplicable:!!rules.countdown_enabled,nextDayDispatch};
  }
  root.C2FDelivery = {deliverySchedule,selectRules,deliveryWindow};
  if (!root.customElements || root.customElements.get('c2f-delivery-module')) return;
  class DeliveryModule extends HTMLElement {
    connectedCallback() {
      if(this.interval)return;
      try {
        this.defaults=JSON.parse(document.getElementById('c2f-commercial-config').textContent);
        this.config=JSON.parse(this.querySelector('[data-c2f-delivery-config]').textContent);
      } catch {return;}
      this.update();this.interval=setInterval(()=>this.update(),1000);
    }
    disconnectedCallback(){clearInterval(this.interval);this.interval=null;}
    setVariant(variant){if(variant){this.variant=variant;this.update();}}
    update(){
      if(!this.config)return;
      const labels=this.config.labels,range=this.querySelector('[data-c2f-delivery-range]'),detail=this.querySelector('[data-c2f-delivery-detail]'),receipt=this.querySelector('[data-c2f-receipt]'),timer=this.querySelector('[data-c2f-dispatch]');
      timer.hidden=true;range.textContent=labels.tracked;receipt.textContent='';
      if(this.dataset.country!=='FR'){detail.textContent=labels.international;return;}
      const lines=this.config.items || [],now=new Date();
      const results=lines.map(line=>{
        const v=this.config.cart?line:(this.variant||line);
        const rules=selectRules(this.defaults,line.rules,v.id);
        return v.available&&rules?.country==='FR'?{rules,schedule:deliverySchedule(rules,now)}:null;
      });
      if(!results.length||results.some(r=>!r?.schedule)){detail.textContent=labels.unavailable;return;}
      const locale=document.documentElement.lang || 'fr-FR';
      const format=d=>new Intl.DateTimeFormat(locale,{day:'numeric',month:'long',timeZone:'UTC'}).format(d);
      const windows=results.map(r=>r.schedule.duration);
      const first=Math.min(...windows.map(w=>w.min)),last=Math.max(...windows.map(w=>w.max));
      const prepMin=Math.min(...windows.map(w=>w.prepMin)),prepMax=Math.max(...windows.map(w=>w.prepMax));
      range.textContent=prepMin===1&&prepMax===2?labels.standard:labels.dispatch.replace('{min}',prepMin).replace('{max}',prepMax);
      const sameBasis=results.every(r=>r.rules.delivery_days_basis===results[0].rules.delivery_days_basis);
      detail.textContent=sameBasis?(results[0].rules.delivery_days_basis==='calendar'?labels.duration:labels.durationBusiness).replace('{min}',first).replace('{max}',last):labels.specific;
      const from=new Date(Math.min(...results.map(r=>+r.schedule.from))),to=new Date(Math.max(...results.map(r=>+r.schedule.to)));
      receipt.textContent=labels.receipt.replace('{from}',format(from)).replace('{to}',format(to));
      const earliest=results.reduce((a,b)=>a.schedule.cutoff<=b.schedule.cutoff?a:b),s=earliest.schedule;
      // Mixed schedules must share an actual preparation cutoff before advertising a cart timer.
      if(results.every(r=>r.schedule.countdownApplicable&&r.schedule.cutoff.getTime()===s.cutoff.getTime())){
        timer.hidden=false;const seconds=Math.max(0,Math.floor(s.remaining/1000));
        this.querySelector('[data-c2f-countdown]').textContent=`${Math.floor(seconds/3600)} h ${String(Math.floor(seconds/60)%60).padStart(2,'0')} min ${String(seconds%60).padStart(2,'0')} s`;
        this.querySelector('[data-c2f-dispatch-promise]').textContent=results.every(r=>r.schedule.nextDayDispatch)?labels.tomorrow:labels.nextDispatch;
      }
    }
  }
  root.customElements.define('c2f-delivery-module',DeliveryModule);
})(typeof window!=='undefined'?window:globalThis);
